import { useEffect, useState } from 'react'
import type { OcorrenciaDto } from '@crm/shared'
import { IconeBuscar, IconeSetaBaixo } from '../../componentes/icones'
import { juntar } from '../../componentes/ui'
import { formatarDataHora } from '../../formato'

/**
 * As setas que andam de uma ocorrência da palavra à outra, dentro da conversa.
 *
 * Sem elas, a busca levava à vez mais recente e as outras continuavam
 * escondidas: numa conversa em que "teste" aparece cinco vezes, as quatro
 * primeiras eram achadas rolando a conversa no olho.
 *
 * A seta para cima anda para trás no tempo, como a rolagem da conversa, e não
 * "para o item anterior da lista". É o mesmo gesto de procurar coisa antiga
 * num histórico, e evita ter de pensar em que direção a lista cresce.
 *
 * Na ponta, em vez de nada acontecer, aparece o aviso de que ali acabou: botão
 * que não responde parece quebrado, e a pessoa fica clicando.
 */
export function NavegarNaBusca({
  palavra,
  ocorrencias,
  indice,
  aoTrocar,
}: {
  palavra: string
  ocorrencias: OcorrenciaDto[]
  /** Qual delas está em foco, de 0 (a mais antiga) ao fim. */
  indice: number
  aoTrocar: (indice: number) => void
}) {
  const [aviso, setAviso] = useState<string | null>(null)

  // O aviso é passageiro: some sozinho, sem exigir um clique para fechar.
  useEffect(() => {
    if (!aviso) return
    const relogio = setTimeout(() => setAviso(null), 3500)
    return () => clearTimeout(relogio)
  }, [aviso])

  if (ocorrencias.length === 0) return null

  const andar = (passo: number) => {
    const destino = indice + passo
    if (destino < 0) {
      setAviso(`Nada mais acima com "${palavra}" nesta conversa.`)
      return
    }
    if (destino >= ocorrencias.length) {
      setAviso(`Nada mais abaixo com "${palavra}" nesta conversa.`)
      return
    }
    setAviso(null)
    aoTrocar(destino)
  }

  const atual = ocorrencias[indice]
  const classeDaSeta =
    'flex h-7 w-7 items-center justify-center rounded-md text-tinta-600 transition hover:bg-amber-100 hover:text-tinta-900 disabled:cursor-default disabled:text-tinta-300 disabled:hover:bg-transparent'

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-1.5">
      <div className="flex items-center gap-2">
        <IconeBuscar className="h-3.5 w-3.5 shrink-0 text-amber-700" />
        <span className="min-w-0 flex-1 truncate text-xs text-tinta-700">
          <strong className="font-semibold">{palavra}</strong>
          {atual ? (
            <span className="text-tinta-500"> · {formatarDataHora(atual.enviadaEm)}</span>
          ) : null}
        </span>

        {/*
          A contagem começa na mais recente: 1 é a última vez que a palavra
          foi dita, e 5 é a mais antiga.

          A lista vem em ordem de tempo, da mais antiga à mais nova, porque é
          assim que a conversa é lida. Quem procura, não: começa pelo que
          acabou de acontecer e vai voltando. Numerar pela ordem da lista
          abria a busca em "5 de 5", como se já estivesse no fim dela.
        */}
        <span className="shrink-0 text-xs tabular-nums text-tinta-600">
          {ocorrencias.length - indice} de {ocorrencias.length}
        </span>

        {/* Para cima é para trás no tempo: a seta aponta como a conversa rola. */}
        <button
          type="button"
          onClick={() => andar(-1)}
          className={classeDaSeta}
          title="Ocorrência anterior, mais antiga"
          aria-label="Ocorrência anterior, mais antiga"
        >
          <IconeSetaBaixo className="h-4 w-4 rotate-180" />
        </button>
        <button
          type="button"
          onClick={() => andar(1)}
          className={classeDaSeta}
          title="Próxima ocorrência, mais recente"
          aria-label="Próxima ocorrência, mais recente"
        >
          <IconeSetaBaixo className="h-4 w-4" />
        </button>
      </div>

      <p
        aria-live="polite"
        className={juntar(
          'text-[11px] text-amber-800 transition-opacity',
          aviso ? 'mt-1 opacity-100' : 'h-0 overflow-hidden opacity-0',
        )}
      >
        {aviso}
      </p>
    </div>
  )
}
